import React from 'react';
import { ShieldCheck, HeartPulse, CheckCircle2, AlertCircle } from 'lucide-react';

interface IntakeSafetyProfileTabProps {
  formData: any;
  handleChange: (field: string, value: any) => void;
}

export const IntakeSafetyProfileTab: React.FC<IntakeSafetyProfileTabProps> = ({
  formData,
  handleChange
}) => {
  return (
    <div className="space-y-4">
      {/* Header / Automatic Data Reuse Banner */}
      <div className="p-3.5 bg-sky-50 border border-sky-200 rounded-lg space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <ShieldCheck className="w-4 h-4 text-sky-700" />
            <span className="font-bold text-sky-800 text-xs">
              Pre-Service Member Support Profile & Safety Orientation
            </span>
          </div>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white text-slate-700 border border-slate-300 font-bold">
            Automatic Chart Reuse Active
          </span>
        </div>
        <p className="text-[11px] text-slate-600 leading-relaxed">
          Member demographics, guardian contacts, payer, PCP, and Support Coordinator are automatically synchronized from chart records. Only the safety, functional, behavioral, and clinician orientation questions below require family confirmation.
        </p>
        <div className="pt-1.5 border-t border-sky-200 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] text-slate-500 font-mono">
          <div>Member: <span className="text-slate-800 font-semibold">{formData.first_name || '—'} {formData.last_name || '—'}</span></div>
          <div>DOB: <span className="text-slate-800 font-semibold">{formData.dob || '—'}</span></div>
          <div>AHCCCS ID: <span className="text-slate-800 font-semibold">{formData.ahcccs_id || '—'}</span></div>
          <div>Support Coord: <span className="text-slate-800 font-semibold">{formData.support_coordinator_name || '—'}</span></div>
        </div>
      </div>

      {/* 1. Medical / Safety */}
      <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs space-y-3">
        <h3 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] flex items-center space-x-1.5">
          <HeartPulse className="w-3.5 h-3.5 text-rose-400" />
          <span>1. Medical & Safety Precautions</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Allergies */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-800 text-xs">Allergies?</div>
                <div className="text-[10px] text-slate-400 mt-0.5">Food, drug, latex, or environmental</div>
              </div>
              <div className="flex items-center space-x-1 bg-slate-100 p-0.5 rounded border border-slate-200">
                <button
                  type="button"
                  onClick={() => handleChange('ddd_allergies', true)}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded transition cursor-pointer ${
                    formData.ddd_allergies ? 'bg-slate-800 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleChange('ddd_allergies', false);
                    handleChange('ddd_allergies_details', '');
                  }}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded transition cursor-pointer ${
                    !formData.ddd_allergies ? 'bg-white text-slate-700 shadow-xs border border-slate-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  No / N/A
                </button>
              </div>
            </div>
            {formData.ddd_allergies && (
              <div>
                <label className="block text-slate-400 text-[10px] mb-1">Specify allergy & reaction protocol</label>
                <input
                  type="text"
                  value={formData.ddd_allergies_details}
                  onChange={e => handleChange('ddd_allergies_details', e.target.value)}
                  placeholder="e.g. Peanut allergy, carries EpiPen"
                  className="w-full bg-white border border-slate-300 rounded px-2.5 py-1.5 text-slate-900 focus:border-slate-500 outline-none text-xs"
                />
              </div>
            )}
          </div>

          {/* Seizures */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-800 text-xs">Seizures?</div>
                <div className="text-[10px] text-slate-400 mt-0.5">History or active seizure disorder</div>
              </div>
              <div className="flex items-center space-x-1 bg-slate-100 p-0.5 rounded border border-slate-200">
                <button
                  type="button"
                  onClick={() => handleChange('ddd_seizures', true)}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded transition cursor-pointer ${
                    formData.ddd_seizures ? 'bg-slate-800 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleChange('ddd_seizures', false);
                    handleChange('ddd_seizures_details', '');
                  }}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded transition cursor-pointer ${
                    !formData.ddd_seizures ? 'bg-white text-slate-700 shadow-xs border border-slate-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  No / N/A
                </button>
              </div>
            </div>
            {formData.ddd_seizures && (
              <div>
                <label className="block text-slate-400 text-[10px] mb-1">Specify seizure type & emergency plan</label>
                <input
                  type="text"
                  value={formData.ddd_seizures_details}
                  onChange={e => handleChange('ddd_seizures_details', e.target.value)}
                  placeholder="e.g. Absence seizures; call 911 if > 3 min"
                  className="w-full bg-white border border-slate-300 rounded px-2.5 py-1.5 text-slate-900 focus:border-slate-500 outline-none text-xs"
                />
              </div>
            )}
          </div>

          {/* Medications */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-800 text-xs">Medications / Special Medical Instructions?</div>
                <div className="text-[10px] text-slate-400 mt-0.5">Routine meds or therapy precautions</div>
              </div>
              <div className="flex items-center space-x-1 bg-slate-100 p-0.5 rounded border border-slate-200">
                <button
                  type="button"
                  onClick={() => handleChange('ddd_medications_needed', true)}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded transition cursor-pointer ${
                    formData.ddd_medications_needed ? 'bg-slate-800 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleChange('ddd_medications_needed', false);
                    handleChange('ddd_medications_details', '');
                  }}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded transition cursor-pointer ${
                    !formData.ddd_medications_needed ? 'bg-white text-slate-700 shadow-xs border border-slate-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  No / N/A
                </button>
              </div>
            </div>
            {formData.ddd_medications_needed && (
              <div>
                <label className="block text-slate-400 text-[10px] mb-1">List medications or therapy-relevant instructions</label>
                <input
                  type="text"
                  value={formData.ddd_medications_details}
                  onChange={e => handleChange('ddd_medications_details', e.target.value)}
                  placeholder="e.g. Daily asthma inhaler on file; nebulizer precautions"
                  className="w-full bg-white border border-slate-300 rounded px-2.5 py-1.5 text-slate-900 focus:border-slate-500 outline-none text-xs"
                />
              </div>
            )}
          </div>

          {/* Assistive Devices */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-800 text-xs">Assistive Devices?</div>
                <div className="text-[10px] text-slate-400 mt-0.5">Glasses, hearing aids, braces, orthotics</div>
              </div>
              <div className="flex items-center space-x-1 bg-slate-100 p-0.5 rounded border border-slate-200">
                <button
                  type="button"
                  onClick={() => handleChange('ddd_assistive_devices_needed', true)}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded transition cursor-pointer ${
                    formData.ddd_assistive_devices_needed ? 'bg-slate-800 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleChange('ddd_assistive_devices_needed', false);
                    handleChange('ddd_assistive_devices_details', '');
                  }}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded transition cursor-pointer ${
                    !formData.ddd_assistive_devices_needed ? 'bg-white text-slate-700 shadow-xs border border-slate-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  No / N/A
                </button>
              </div>
            </div>
            {formData.ddd_assistive_devices_needed && (
              <div>
                <label className="block text-slate-400 text-[10px] mb-1">Specify equipment or adaptive devices</label>
                <input
                  type="text"
                  value={formData.ddd_assistive_devices_details}
                  onChange={e => handleChange('ddd_assistive_devices_details', e.target.value)}
                  placeholder="e.g. Corrective glasses; bilateral AFOs"
                  className="w-full bg-white border border-slate-300 rounded px-2.5 py-1.5 text-slate-900 focus:border-slate-500 outline-none text-xs"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2. Functional Support */}
      <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs space-y-3">
        <h3 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] flex items-center space-x-1.5">
          <HeartPulse className="w-3.5 h-3.5 text-sky-400" />
          <span>2. Functional Accommodations</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Feeding Support */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-800 text-xs">Feeding / Swallowing Support?</div>
                <div className="text-[10px] text-slate-400 mt-0.5">Dysphagia, choking, or picky eating</div>
              </div>
              <div className="flex items-center space-x-1 bg-slate-100 p-0.5 rounded border border-slate-200">
                <button
                  type="button"
                  onClick={() => handleChange('ddd_feeding_needed', true)}
                  className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                    formData.ddd_feeding_needed ? 'bg-slate-800 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleChange('ddd_feeding_needed', false);
                    handleChange('ddd_feeding_details', '');
                  }}
                  className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                    !formData.ddd_feeding_needed ? 'bg-white text-slate-700 shadow-xs border border-slate-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  No / N/A
                </button>
              </div>
            </div>
            {formData.ddd_feeding_needed && (
              <div>
                <label className="block text-slate-400 text-[10px] mb-1">Details & precautions</label>
                <input
                  type="text"
                  value={formData.ddd_feeding_details}
                  onChange={e => handleChange('ddd_feeding_details', e.target.value)}
                  placeholder="e.g. Pureed diet, choking precautions"
                  className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-slate-900 focus:border-slate-500 outline-none text-xs"
                />
              </div>
            )}
          </div>

          {/* Mobility Support */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-800 text-xs">Mobility / Transfer Support?</div>
                <div className="text-[10px] text-slate-400 mt-0.5">Gait, balance, transfer assistance</div>
              </div>
              <div className="flex items-center space-x-1 bg-slate-100 p-0.5 rounded border border-slate-200">
                <button
                  type="button"
                  onClick={() => handleChange('ddd_mobility_needed', true)}
                  className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                    formData.ddd_mobility_needed ? 'bg-slate-800 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleChange('ddd_mobility_needed', false);
                    handleChange('ddd_mobility_details', '');
                  }}
                  className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                    !formData.ddd_mobility_needed ? 'bg-white text-slate-700 shadow-xs border border-slate-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  No / N/A
                </button>
              </div>
            </div>
            {formData.ddd_mobility_needed && (
              <div>
                <label className="block text-slate-400 text-[10px] mb-1">Details & assistance level</label>
                <input
                  type="text"
                  value={formData.ddd_mobility_details}
                  onChange={e => handleChange('ddd_mobility_details', e.target.value)}
                  placeholder="e.g. Needs contact-guard assistance on stairs"
                  className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-slate-900 focus:border-slate-500 outline-none text-xs"
                />
              </div>
            )}
          </div>

          {/* AAC / Communication Support */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-800 text-xs">AAC / Special Communication?</div>
                <div className="text-[10px] text-slate-400 mt-0.5">Nonverbal, PECS, speech app</div>
              </div>
              <div className="flex items-center space-x-1 bg-slate-100 p-0.5 rounded border border-slate-200">
                <button
                  type="button"
                  onClick={() => handleChange('ddd_communication_needed', true)}
                  className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                    formData.ddd_communication_needed ? 'bg-slate-800 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleChange('ddd_communication_needed', false);
                    handleChange('ddd_communication_details', '');
                  }}
                  className={`px-2 py-0.5 text-[10px] font-bold rounded transition cursor-pointer ${
                    !formData.ddd_communication_needed ? 'bg-white text-slate-700 shadow-xs border border-slate-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  No / N/A
                </button>
              </div>
            </div>
            {formData.ddd_communication_needed && (
              <div>
                <label className="block text-slate-400 text-[10px] mb-1">Details & AAC device/app</label>
                <input
                  type="text"
                  value={formData.ddd_communication_details}
                  onChange={e => handleChange('ddd_communication_details', e.target.value)}
                  placeholder="e.g. iPad with TouchChat AAC system"
                  className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-slate-900 focus:border-slate-500 outline-none text-xs"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3. Behavior / Safety */}
      <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] flex items-center space-x-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
            <span>3. Behavior & Safety Considerations</span>
          </h3>
          <div className="flex items-center space-x-1 bg-slate-100 p-0.5 rounded border border-slate-200">
            <button
              type="button"
              onClick={() => handleChange('ddd_behavior_needed', true)}
              className={`px-2.5 py-1 text-[11px] font-bold rounded transition cursor-pointer ${
                formData.ddd_behavior_needed ? 'bg-slate-800 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => {
                handleChange('ddd_behavior_needed', false);
                handleChange('ddd_behavior_types', []);
                handleChange('ddd_behavior_description', '');
                handleChange('ddd_behavior_frequency', '');
                handleChange('ddd_behavior_intervention', '');
                handleChange('ddd_behavior_plan_exists', false);
              }}
              className={`px-2.5 py-1 text-[11px] font-bold rounded transition cursor-pointer ${
                !formData.ddd_behavior_needed ? 'bg-white text-slate-700 shadow-xs border border-slate-200' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              No / N/A
            </button>
          </div>
        </div>

        <div className="text-[11px] text-slate-400">
          Any behavior or safety support needs staff should know about?
        </div>

        {!formData.ddd_behavior_needed ? (
          <div className="p-2.5 bg-slate-50 border border-slate-200 rounded text-slate-600 text-xs flex items-center space-x-2">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400"></span>
            <span>No significant behavior concerns reported. Standard clinical rapport and positive engagement apply.</span>
          </div>
        ) : (
          <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div>
              <label className="block text-slate-700 text-[10px] font-semibold mb-1.5 uppercase">
                Select Observed Behaviors / Safety Concerns:
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  'Aggression',
                  'Self-injury',
                  'Elopement',
                  'Property destruction',
                  'Transition difficulty',
                  'Food/non-food seeking',
                  'Other'
                ].map(type => {
                  const currentTypes = Array.isArray(formData.ddd_behavior_types) ? formData.ddd_behavior_types : [];
                  const checked = currentTypes.includes(type);
                  return (
                    <label
                      key={type}
                      className={`flex items-center space-x-2 p-1.5 rounded cursor-pointer border text-xs transition ${
                        checked
                          ? 'bg-amber-50 border-amber-300 text-amber-900'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => {
                          if (e.target.checked) {
                            handleChange('ddd_behavior_types', [...currentTypes, type]);
                          } else {
                            handleChange('ddd_behavior_types', currentTypes.filter(t => t !== type));
                          }
                        }}
                        className="rounded text-slate-700 bg-white border-slate-300"
                      />
                      <span>{type}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div>
                <label className="block text-slate-400 text-[10px] mb-1">Short Description</label>
                <input
                  type="text"
                  value={formData.ddd_behavior_description}
                  onChange={e => handleChange('ddd_behavior_description', e.target.value)}
                  placeholder="e.g. May attempt to leave room during non-preferred tasks"
                  className="w-full bg-white border border-slate-300 rounded px-2.5 py-1.5 text-slate-900 focus:border-slate-500 outline-none text-xs"
                />
              </div>
              <div>
                <label className="block text-slate-400 text-[10px] mb-1">Approximate Frequency</label>
                <input
                  type="text"
                  value={formData.ddd_behavior_frequency}
                  onChange={e => handleChange('ddd_behavior_frequency', e.target.value)}
                  placeholder="e.g. Rare, during difficult transitions"
                  className="w-full bg-white border border-slate-300 rounded px-2.5 py-1.5 text-slate-900 focus:border-slate-500 outline-none text-xs"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-400 text-[10px] mb-1">Recommended Support / Intervention Strategy</label>
              <input
                type="text"
                value={formData.ddd_behavior_intervention}
                onChange={e => handleChange('ddd_behavior_intervention', e.target.value)}
                placeholder="e.g. Visual timer, first/then boards, choice-offering"
                className="w-full bg-white border border-slate-300 rounded px-2.5 py-1.5 text-slate-900 focus:border-slate-500 outline-none text-xs"
              />
            </div>

            <label className="flex items-center space-x-2 pt-1 cursor-pointer">
              <input
                type="checkbox"
                checked={!!formData.ddd_behavior_plan_exists}
                onChange={e => handleChange('ddd_behavior_plan_exists', e.target.checked)}
                className="rounded text-slate-700 bg-white border-slate-300"
              />
              <span className="text-slate-700 text-xs">Formal Behavior Support Plan (BTP/FBA) or Crisis Plan on file</span>
            </label>
          </div>
        )}
      </div>

      {/* 4. Specialized Training */}
      <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] flex items-center space-x-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-purple-400" />
            <span>4. Member-Specific Staff Training</span>
          </h3>
          <div className="flex items-center space-x-1 bg-slate-100 p-0.5 rounded border border-slate-200">
            <button
              type="button"
              onClick={() => handleChange('ddd_training_needed', true)}
              className={`px-2.5 py-1 text-[11px] font-bold rounded transition cursor-pointer ${
                formData.ddd_training_needed ? 'bg-slate-800 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => {
                handleChange('ddd_training_needed', false);
                handleChange('ddd_training_topics', []);
                handleChange('ddd_training_details', '');
              }}
              className={`px-2.5 py-1 text-[11px] font-bold rounded transition cursor-pointer ${
                !formData.ddd_training_needed ? 'bg-white text-slate-700 shadow-xs border border-slate-200' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              No / N/A
            </button>
          </div>
        </div>

        <div className="text-[11px] text-slate-400">
          Does Allegiant staff require any member-specific training before treatment?
        </div>

        {!formData.ddd_training_needed ? (
          <div className="p-2.5 bg-slate-50 border border-slate-200 rounded text-slate-600 text-xs flex items-center space-x-2">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400"></span>
            <span>Standard licensed clinical competencies apply (Article 9, CPR/First Aid, Level One Fingerprint Card).</span>
          </div>
        ) : (
          <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div>
              <label className="block text-slate-700 text-[10px] font-semibold mb-1.5 uppercase">
                Training Topics Required Prior to Care:
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  'Medication protocol',
                  'Seizure rescue protocol',
                  'Feeding & swallowing plan',
                  'Behavior support plan',
                  'Mobility/transfer technique',
                  'Other'
                ].map(topic => {
                  const currentTopics = Array.isArray(formData.ddd_training_topics) ? formData.ddd_training_topics : [];
                  const checked = currentTopics.includes(topic);
                  return (
                    <label
                      key={topic}
                      className={`flex items-center space-x-2 p-1.5 rounded cursor-pointer border text-xs transition ${
                        checked
                          ? 'bg-slate-100 border-slate-300 text-slate-800'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => {
                          if (e.target.checked) {
                            handleChange('ddd_training_topics', [...currentTopics, topic]);
                          } else {
                            handleChange('ddd_training_topics', currentTopics.filter(t => t !== topic));
                          }
                        }}
                        className="rounded text-slate-700 bg-white border-slate-300"
                      />
                      <span>{topic}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-slate-400 text-[10px] mb-1">Specify Orientation / Training Details</label>
              <input
                type="text"
                value={formData.ddd_training_details}
                onChange={e => handleChange('ddd_training_details', e.target.value)}
                placeholder="e.g. 15-minute caregiver orientation on transfer technique"
                className="w-full bg-white border border-slate-300 rounded px-2.5 py-1.5 text-slate-900 focus:border-slate-500 outline-none text-xs"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
